// Shared types for Energy Flow 2D and 3D modes

export type AssetType = 'solar' | 'battery' | 'grid' | 'house' | 'ev_charger' | 'heatpump' | 'custom';
export type FlowShape = 'dash' | 'circle' | 'square';
export type LineType = 'curved' | 'straight' | 'orthogonal';
export type ViewMode = '2d' | '3d';
export type CameraPreset = 'isometric' | 'topDown' | 'firstPerson' | 'side';

export const CAMERA_PRESETS: Record<CameraPreset, {
  name: string;
  position: [number, number, number];
  target: [number, number, number];
}> = {
  isometric: {
    name: 'Isometrisch',
    position: [20, 15, 20],
    target: [0, 0, 0],
  },
  topDown: {
    name: 'Bovenaanzicht',
    position: [0, 40, 0.1],
    target: [0, 0, 0],
  },
  firstPerson: {
    name: 'First Person',
    position: [0, 2, 15],
    target: [0, 1, 0],
  },
  side: {
    name: 'Zijaanzicht',
    position: [30, 5, 0],
    target: [0, 2, 0],
  },
};

export interface Node {
  id: string;
  type: AssetType;
  x: number;
  y: number;
  label?: string;
  customImageSrc?: string | null;
  customModelUrl?: string; // For 3D GLTF models (.glb, .gltf)
  scale?: number;
  baseValue?: number;
  currentValue?: number;
  batteryCharge?: number;
  status?: 'ok' | 'warning' | 'error';
  animScale?: number;
  pulsePhase?: number;
  _displayScale?: number;
  clickUrl?: string;
  clickTarget?: '_self' | '_blank' | '_parent';
}

export interface Connection {
  id: string;
  fromId: string;
  toId: string;
  color: string;
  speed: number;
  width: number;
  direction: 'forward' | 'reverse';
  shape: FlowShape;
  activeInDay: boolean;
  activeInNight: boolean;
  load?: number;
  lineType: LineType;
  particleColor: string;
  particleSize: number;
}

export interface EnergyFlow3DViewProps {
  nodes: Node[];
  connections: Connection[];
  selectedEntityId: string | null;
  onSelectNode: (id: string | null) => void;
  onSelectConnection: (id: string | null) => void;
  onUpdateNode: (id: string, updates: Partial<Node>) => void;
  isSimulating: boolean;
  timeOfDay: number;
  weatherValue: number;
  showGrid: boolean;
  backgroundColor: string;
  showShadows?: boolean;
  selectedTool?: string | null;
  onPlaceAsset?: (position: { x: number; y: number }) => void;
}

// Asset default configurations
export const ASSET_3D_CONFIG: Record<AssetType, {
  color: string;
  emissive?: string;
  scale: [number, number, number];
  geometry: 'box' | 'cylinder' | 'cone' | 'custom';
}> = {
  solar: { color: '#2563eb', emissive: '#1e40af', scale: [2, 0.1, 1.5], geometry: 'box' },
  battery: { color: '#f8fafc', scale: [1.2, 2, 1.2], geometry: 'box' },
  grid: { color: '#64748b', scale: [0.3, 3, 0.3], geometry: 'cylinder' },
  house: { color: '#f1f5f9', scale: [2.5, 1.5, 4], geometry: 'box' },
  ev_charger: { color: '#22c55e', scale: [0.4, 1.5, 0.4], geometry: 'cylinder' },
  heatpump: { color: '#06b6d4', scale: [1, 0.8, 1], geometry: 'box' },
  custom: { color: '#8b5cf6', scale: [1, 1, 1], geometry: 'box' },
};
